// NewCustomerFields (Domain/Customers, BL-189): the new-customer form as All customers' Add customer
// draws it in Blueprint (QA-323; apps/web shared/NewCustomer.tsx's form, the same fields, words and
// checks, on catalogued parts). The name, mobile, email, service address, city and type; then the consent
// asked, saved with who and when; then, when what's typed is already a customer's, a notice that names
// them, with a button to open them and, once saving stopped on it, Add anyway (it never blocks: a tenant
// or a new owner can share them, F-41); then the action and Cancel, an underlined word as Add anyway
// is, a refusal on its own line under them.
// It holds no state: the app's form (useNewCustomer) gives it the values and the notice. Reads only
// --newcust-*; Field, Checkbox, Button and Link read their own.
import { useId, type ReactNode } from 'react';
import { Button } from '../Button/Button.tsx';
import { Checkbox } from '../Checkbox/Checkbox.tsx';
import { Link } from '../Link/Link.tsx';
import { SelectField, TextField } from '../Field/Field.tsx';
import './NewCustomerFields.css';

export interface NewCustomerValues {
  name: string;
  mobile: string;
  /** OK to text about their appointments and invoices. */
  sms: boolean;
  email: string;
  /** OK to email. */
  mail: boolean;
  line1: string;
  city: string;
  kind: 'residential' | 'commercial';
}

export interface NewCustomerFieldsProps {
  values: NewCustomerValues;
  onChange: (patch: Partial<NewCustomerValues>) => void;
  /** Leaving a field the duplicate check reads (the mobile, the email, the address, the city). */
  onCheck: () => void;
  /** A call's number is known: no mobile is asked, and texting is asked on the call. */
  hasPhone?: boolean;
  /** Over the consent: "Consent (saved with who and when)". */
  consentNote: string;
  submitLabel: string;
  /** The -ing form while it saves ("Adding the customer…"). */
  busyLabel: string;
  isBusy?: boolean;
  isSubmitDisabled?: boolean;
  /** Why the last try failed, under the buttons. */
  error?: string | null;
  onSubmit: () => void;
  onCancel?: () => void;
  /** What's typed is already a customer's: the notice, picking them, and Add anyway once it held saving. */
  dupe?: {
    text: ReactNode;
    pickLabel: string;
    onPick: () => void;
    /** Saving stopped on it: the notice is said at once, and Add anyway is offered. */
    isHeld: boolean;
    onAddAnyway?: () => void;
  } | null;
  /** The check couldn't run and saving stopped on that: what it says, and Add anyway. */
  unchecked?: { text: ReactNode; onAddAnyway: () => void } | null;
}

export function NewCustomerFields({
  values: f,
  onChange,
  onCheck,
  hasPhone = false,
  consentNote,
  submitLabel,
  busyLabel,
  isBusy = false,
  isSubmitDisabled = false,
  error,
  onSubmit,
  onCancel,
  dupe,
  unchecked,
}: NewCustomerFieldsProps) {
  const consentId = useId();
  return (
    <div className="fl-newcust">
      <div className="fl-newcust__grid">
        <TextField label="Name" value={f.name} onChange={(name) => onChange({ name })} autoFocus />
        {!hasPhone ? (
          <TextField
            label="Mobile"
            type="tel"
            value={f.mobile}
            onChange={(mobile) => onChange({ mobile })}
            onBlur={onCheck}
            placeholder="optional"
          />
        ) : null}
        <TextField
          label="Email"
          type="email"
          value={f.email}
          onChange={(email) => onChange({ email })}
          onBlur={onCheck}
          placeholder="optional"
        />
        <TextField
          label="Service address"
          value={f.line1}
          onChange={(line1) => onChange({ line1 })}
          onBlur={onCheck}
        />
        <TextField label="City" value={f.city} onChange={(city) => onChange({ city })} onBlur={onCheck} />
        <SelectField
          label="Type"
          value={f.kind}
          onChange={(kind) => onChange({ kind: kind as NewCustomerValues['kind'] })}
          options={[
            { value: 'residential', label: 'Residential' },
            { value: 'commercial', label: 'Commercial' },
          ]}
        />
      </div>
      <div className="fl-newcust__consent" role="group" aria-labelledby={consentId}>
        <span className="fl-newcust__label" id={consentId}>
          {consentNote}
        </span>
        <Checkbox
          size="touch-on-phone"
          isSelected={f.sms}
          onChange={(sms) => onChange({ sms })}
          isDisabled={!hasPhone && !f.mobile.trim()}
        >
          OK to text about their appointments and invoices
        </Checkbox>
        <Checkbox
          size="touch-on-phone"
          isSelected={f.mail}
          onChange={(mail) => onChange({ mail })}
          isDisabled={!f.email.trim()}
        >
          OK to email
        </Checkbox>
      </div>
      {dupe ? (
        <div className="fl-newcust__dupe" role={dupe.isHeld ? 'alert' : undefined}>
          <p className="fl-newcust__say">{dupe.text}</p>
          <div className="fl-newcust__acts">
            <Button size="sm" isDisabled={isBusy} onPress={dupe.onPick}>
              {dupe.pickLabel}
            </Button>
            {dupe.isHeld && dupe.onAddAnyway ? (
              <Link size="touch-on-phone" isDisabled={isBusy} onPress={dupe.onAddAnyway}>
                Add anyway
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}
      {unchecked && !dupe ? (
        <div className="fl-newcust__dupe" role="alert">
          <p className="fl-newcust__say">{unchecked.text}</p>
          <div className="fl-newcust__acts">
            <Link size="touch-on-phone" isDisabled={isBusy} onPress={unchecked.onAddAnyway}>
              Add anyway
            </Link>
          </div>
        </div>
      ) : null}
      <div className="fl-newcust__foot">
        <div className="fl-newcust__acts">
          <Button
            variant="primary"
            isBusy={isBusy}
            isDisabled={isSubmitDisabled && !isBusy}
            onPress={() => {
              if (!isBusy) onSubmit();
            }}
          >
            {isBusy ? busyLabel : submitLabel}
          </Button>
          {onCancel ? (
            <Link size="touch-on-phone" isDisabled={isBusy} onPress={onCancel}>
              Cancel
            </Link>
          ) : null}
        </div>
        {error && !isBusy ? (
          <p className="fl-newcust__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
